import { GET_ORDER_CONSTRAINTS_QUERY } from '@/contexts/variant-pack-info-context-queries';
import {
  OfflineBackgroundSyncRegistryAdapter,
  OfflineOrderConstraintsAdapter,
} from '@/adapters/offline-mode';
import { OfflineSyncType } from '@/utils/offline-mode';
import { print as printQuery } from 'graphql';
import { useLazyQuery } from '@/hooks/use-lazy-query';
import {
  VariantPackInfoContext_OrderConstraintsQuery,
  VariantPackInfoContext_OrderConstraintsQueryVariables,
} from '@/types';

/**
 * Custom hook to download and save the order constraints for a specific restaurant and supplier.
 *
 * This hook uses a lazy query to fetch the order constraints from the server and then saves them
 * locally using the OfflineOrderConstraintsAdapter.
 *
 * @returns {Object} - An object containing the downloadOrderConstraints function.
 *
 * @function
 * @name useDownloadOrderConstraints
 *
 * @example
 * const { downloadOrderConstraints } = useDownloadOrderConstraints();
 *
 * // Download and save order constraints for a specific restaurant and supplier
 * downloadOrderConstraints('restaurant-uuid', 'supplier-uuid');
 */
export const useDownloadOrderConstraints = () => {
  const getOrderConstraints = useLazyQuery<
    VariantPackInfoContext_OrderConstraintsQuery,
    VariantPackInfoContext_OrderConstraintsQueryVariables
  >(GET_ORDER_CONSTRAINTS_QUERY, {
    fetchPolicy: 'network-only',
  });

  /**
   * Fetches and saves the order constraints for a given restaurant and supplier.
   *
   * @async
   * @param {string} restaurantUUID - The UUID of the restaurant.
   * @param {string} supplierUUID - The UUID of the supplier.
   */
  const downloadOrderConstraints = async (
    restaurantUUID: string,
    supplierUUID: string,
  ) => {
    const variables = {
      restaurantUUID,
      supplierUUID,
    };

    const { data: orderConstraintsData } = await getOrderConstraints({
      context: {
        offlineSync: true,
      },
      variables,
    });
    const offlineOrderConstraintsAdapter = new OfflineOrderConstraintsAdapter(
      restaurantUUID,
      supplierUUID,
    );
    await offlineOrderConstraintsAdapter.save(orderConstraintsData);

    const backgroundSyncRegistryAdapter =
      new OfflineBackgroundSyncRegistryAdapter();
    await backgroundSyncRegistryAdapter.updateSyncMetadata(
      restaurantUUID,
      supplierUUID,
      {
        query: printQuery(GET_ORDER_CONSTRAINTS_QUERY),
        syncType: OfflineSyncType.OrderConstraints,
        variables,
      },
    );
  };

  return { downloadOrderConstraints };
};
